// ─────────────────────────────────────────────────────────────────────────────
// BizPlus parity — per-user access editor  (Phase 0.2, wiring)
//
// Two levels, in the order they apply:
//   1. MODULES  — which workspace areas the person holds at all.
//   2. SCREENS  — optionally narrow those modules to named screens.
//
// Screen narrowing is opt-in. Left off, the user keeps seeing everything in
// their modules, which is how every account created before section grants
// existed behaves — turning it on for someone who does not need it would
// quietly shrink their access.
//
// Saving REPLACES the whole permissions array, so the payload is composed by
// buildAccessPayload (pure and tested) rather than assembled here.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, Search } from 'lucide-react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { cn } from '@/shared/utils/utils';
import {
  WORKSPACE_ACCESS_CATALOG,
  WORKSPACE_ACCESS_PROFILE,
  ALL_WORKSPACE_ACCESS,
  defaultWorkspaceAccess,
  type WorkspaceAccessKey,
} from '@/shared/permissions/workspaceAccess';
import {
  sectionsForModules,
  sectionPathFromPermission,
  WORKSPACE_SECTIONS_PROFILE,
} from '@/shared/permissions/moduleSections';
import { buildAccessPayload } from './accessPayload';
import type { HrUser } from '../types';

interface UserAccessModalProps {
  user: HrUser | null;
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onSubmit: (permissions: string[]) => void;
}

/**
 * Read a user's stored permissions back into the editor's state.
 *
 * A user whose permissions were never explicitly configured (no
 * `workspace.access.configured` marker) is NOT running on an empty module
 * list today — they're running on their department defaults, exactly as
 * effectiveWorkspaceAccess() resolves it everywhere else in the app (see
 * workspaceAccess.ts on the frontend and workspace-access.js on the
 * backend — both fall back to DEPARTMENT_WORKSPACE_DEFAULTS, merged with
 * any stray workspace.* grants, when the marker is absent). Showing
 * "0 enabled" here would be a lie the editor tells about someone's real
 * access, and saving from that false starting point would silently lock
 * them out of everything the defaults currently grant them.
 */
function readExisting(user: HrUser | null) {
  const permissions = user?.permissions ?? [];
  if (user?.role === 'superadmin') {
    return { modules: [...ALL_WORKSPACE_ACCESS], restrict: false, sections: [] };
  }

  const configured = permissions.includes(WORKSPACE_ACCESS_PROFILE);
  const granted = WORKSPACE_ACCESS_CATALOG
    .map(({ key }) => key)
    .filter((key) => permissions.includes(`workspace.${key}`));
  const modules = configured
    ? granted
    : [...new Set([...defaultWorkspaceAccess(user?.department ?? ''), ...granted])];
  const restrict = permissions.includes(WORKSPACE_SECTIONS_PROFILE);
  const sections = permissions
    .map(sectionPathFromPermission)
    .filter((p): p is string => p !== null);
  return { modules, restrict, sections };
}

export function UserAccessModal({ user, open, loading, onClose, onSubmit }: UserAccessModalProps) {
  const [modules, setModules] = useState<WorkspaceAccessKey[]>([]);
  const [restrict, setRestrict] = useState(false);
  const [sections, setSections] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Re-read whenever a different user is opened, so the editor never shows the
  // previous person's access.
  useEffect(() => {
    if (!open) return;
    const existing = readExisting(user);
    setModules(existing.modules);
    setRestrict(existing.restrict);
    setSections(existing.sections);
    setSearch('');
  }, [open, user]);

  // Only screens inside the modules they hold can be granted — offering others
  // would produce a grant the module gate blocks anyway.
  const available = useMemo(() => sectionsForModules(modules), [modules]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return available;
    return available.filter(
      (s) => s.label.toLowerCase().includes(term) || s.path.includes(term),
    );
  }, [available, search]);

  const toggleModule = (key: WorkspaceAccessKey) => {
    setModules((prev) => {
      const next = prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key];
      // Drop screen grants that belong to a module just removed, so a stale
      // grant cannot linger and silently reappear if the module returns.
      const stillValid = new Set(sectionsForModules(next).map((s) => s.path));
      setSections((paths) => paths.filter((p) => stillValid.has(p)));
      return next;
    });
  };

  const toggleSection = (path: string) => {
    setSections((prev) => (prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path]));
  };

  const submit = () => {
    onSubmit(buildAccessPayload({ modules, restrictSections: restrict, sections }));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Access — ${user?.display_name ?? ''}`}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button onClick={submit} loading={loading}>Save access</Button>
        </>
      }
    >
      <div className="space-y-5">
        <section className="space-y-2">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-on-surface-variant" aria-hidden />
            <h3 className="text-data-md font-semibold text-on-surface">Modules</h3>
            <span className="text-body-sm text-on-surface-variant">
              {modules.length} enabled
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {WORKSPACE_ACCESS_CATALOG.map((m) => {
              const on = modules.includes(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => toggleModule(m.key)}
                  className={cn(
                    'rounded-full border px-2.5 py-1 text-[12px] transition-colors',
                    on
                      ? 'border-primary bg-primary-container text-on-primary-container'
                      : 'border-outline text-on-surface-variant hover:border-primary',
                  )}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </section>

        <section className="space-y-3 border-t border-outline pt-4">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={restrict}
              onChange={(e) => setRestrict(e.target.checked)}
              className="mt-1"
            />
            <span>
              <span className="block text-data-md font-medium text-on-surface">
                Restrict to specific screens
              </span>
              <span className="block text-body-sm text-on-surface-variant">
                Off means they see every screen in the modules above — the normal
                setting. Turn it on only to narrow someone further.
              </span>
            </span>
          </label>

          {restrict && (
            <div className="space-y-3">
              <Input
                leftIcon={<Search className="h-4 w-4" />}
                placeholder="Filter screens…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {available.length === 0 ? (
                <p className="text-body-sm text-on-surface-variant">
                  Enable a module above to choose screens.
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-3 text-body-sm">
                    <span className="text-on-surface-variant">
                      {sections.length} of {available.length} screens allowed
                    </span>
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setSections(available.map((s) => s.path))}
                    >
                      all
                    </button>
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setSections([])}
                    >
                      none
                    </button>
                  </div>
                  {sections.length === 0 && (
                    <p className="text-body-sm text-warning-text">
                      No screens selected — this person will not be able to open
                      anything. Untick “Restrict to specific screens” to give
                      them their full modules back.
                    </p>
                  )}
                  <div className="max-h-64 space-y-1 overflow-y-auto rounded-lg border border-outline p-2">
                    {visible.map((s) => (
                      <label
                        key={s.path}
                        className="flex items-center gap-2 rounded px-2 py-1 hover:bg-surface-variant/50"
                      >
                        <input
                          type="checkbox"
                          checked={sections.includes(s.path)}
                          onChange={() => toggleSection(s.path)}
                        />
                        <span className="text-data-md text-on-surface">{s.label}</span>
                        <span className="ml-auto text-[11px] text-on-surface-variant">{s.path}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </section>
      </div>
    </Modal>
  );
}
